import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import axios from 'axios';
import Message from './Message';
import MicrophoneButton from './MicrophoneButton';
import SuggestionButton from './SuggestionButton';
import sendIcon from '../assets/send.svg';

const ChatContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  background: none;
  border-radius: 10px;
  overflow: hidden;
  margin-top: 20px;
  opacity: ${props => props.$visible ? 1 : 0};
  max-height: ${props => props.$visible ? '1000px' : '0'};
  transition: opacity 0.3s ease, max-height 0.5s ease;
  width: 100%;
  z-index: 5;
  margin-bottom: 20px; /* Add space at the bottom */

  /* Ensure proper spacing on mobile */
  @media (max-width: 767px) {
    margin-bottom: 30px;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
  display: flex;
  flex-direction: column; /* Standard direction */
  gap: 16px;
  min-height: 300px;
  width: 100%;
  background-color: rgba(255, 255, 255, 0.7);
  backdrop-filter: blur(5px);
  border-radius: 10px;
  justify-content: flex-start; /* Align items to the top */
  margin-bottom: 20px; /* Add some space at the bottom */

  /* Ensure the container doesn't extend too far on mobile */
  @media (max-width: 767px) {
    max-height: calc(100vh - 250px);
    min-height: 200px;
  }
`;

const InputContainer = styled.div`
  display: flex;
  position: relative;
  margin-bottom: 20px;
  width: 100%;
  z-index: 10;
  opacity: 1; /* Always visible */
  transition: opacity 0.3s ease;
`;

const fadeInOut = keyframes`
  0% { opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { opacity: 0; }
`;

const MessageInput = styled.input`
  flex: 1;
  padding: 12px 16px;
  padding-right: 100px;
  border: 1px solid #e0e0e0;
  border-radius: 24px;
  font-size: 16px;
  outline: none;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.05);
  background-color: white;
  transition: border-color 0.3s;

  &:focus {
    border-color: var(--primary-color-transparent);
    box-shadow: 0 2px 8px rgba(44, 94, 110, 0.1);
  }

  &::placeholder {
    color: var(--primary-color);
    opacity: 0.7;
  }
`;

const PlaceholderText = styled.div`
  position: absolute;
  left: 16px;
  top: 50%;
  transform: translateY(-50%);
  color: var(--primary-color);
  opacity: 0.7;
  pointer-events: none;
  animation: ${fadeInOut} 3s ease-in-out;
  animation-fill-mode: forwards;
  z-index: 5;
  display: ${props => props.$show ? 'block' : 'none'};
`;

const SendButton = styled.button`
  background: none;
  color: var(--primary-color);
  border: none;
  width: 36px;
  height: 36px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  cursor: pointer;
  transition: color 0.2s;
  z-index: 10;

  &:hover {
    color: var(--primary-color-transparent);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const SendIcon = styled.img`
  width: 24px;
  height: 24px;
`;

const SuggestionsContainer = styled.div`
  display: flex;
  justify-content: center;
  gap: 12px;
  padding: 20px 0;
  flex-wrap: wrap;
  width: 100%;
  margin: 0 auto;
  position: fixed;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, rgba(255, 255, 255, 1) 100%);
  padding-top: 40px; /* Add extra padding at the top to make the gradient more visible */
  z-index: 10;
  max-width: 800px;
  opacity: 1; /* Always visible */
  transition: opacity 0.3s ease;

  /* Improve layout for suggestions on mobile */
  @media (max-width: 767px) {
    gap: 10px;
    padding: 15px 10px; /* Add horizontal padding */
    padding-top: 30px;
    width: calc(100% - 20px); /* Account for padding */
    justify-content: space-evenly; /* Better distribute space */
  }
`;

const DisclaimerNote = styled.div`
  font-size: 12px;
  color: rgba(0, 0, 0, 0.5);
  text-align: center;
  margin: 8px 0;
  font-style: italic;
  opacity: ${props => props.$visible ? 0.8 : 0};
  transition: opacity 0.3s ease;
`;

const RefreshButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: rgba(0, 0, 0, 0.3);
  font-size: 12px;
  cursor: pointer;
  padding: 5px;

  &:hover {
    color: rgba(0, 0, 0, 0.6);
  }
`;

const MainContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 800px;
  margin: 0 auto;
  height: 100%;
  position: relative;
  padding-bottom: 120px; /* Increased space for fixed suggestions */
  z-index: 5;

  /* Add even more padding on mobile */
  @media (max-width: 767px) {
    padding-bottom: 150px;
  }
`;

// Add a scroll indicator component
const ScrollIndicator = styled.div`
  position: absolute;
  bottom: 160px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 255, 255, 0.9);
  color: var(--primary-color);
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 12px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
  display: ${props => props.$visible ? 'block' : 'none'};
  animation: fadeInOut 2s infinite;
  z-index: 15;
  pointer-events: none;

  @media (max-width: 767px) {
    bottom: 140px;
  }
`;

// Multilingual suggestions with more complete options - moved outside component
const suggestionsByLanguage = {
  en: [
    "I want to build a digital solution for my business",
    "I'm looking to rent an apartment in Porto for my vacation",
    "How can I get in touch with your team?"
  ],
  pt: [
    "Quero construir uma solução digital para o meu negócio",
    "Estou à procura de um apartamento para alugar no Porto para férias",
    "Como posso entrar em contacto com a vossa equipa?"
  ],
  fr: [
    "Je souhaite créer une solution numérique pour mon entreprise",
    "Je cherche à louer un appartement à Porto pour mes vacances",
    "Comment puis-je contacter votre équipe?"
  ],
  es: [
    "Quiero construir una solución digital para mi negocio",
    "Estoy buscando alquilar un apartamento en Oporto para mis vacaciones",
    "¿Cómo puedo ponerme en contacto con vuestro equipo?"
  ],
  de: [
    "Ich möchte eine digitale Lösung für mein Unternehmen entwickeln",
    "Ich suche eine Wohnung zur Miete in Porto für meinen Urlaub",
    "Wie kann ich mit Ihrem Team Kontakt aufnehmen?"
  ]
};

// Placeholders for different languages - moved outside component
const placeholders = [
  "How can I help?", // English
  "Como posso ajudar?", // Portuguese
  "Comment puis-je aider?", // French
  "Wie kann ich helfen?", // German
  "¿Cómo puedo ayudar?", // Spanish
];

const ChatInterface = () => {
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const [placeholderIndex, setPlaceholderIndex] = useState(0);
  const [showPlaceholder, setShowPlaceholder] = useState(true);
  const [currentLanguage, setCurrentLanguage] = useState('en'); // Default language is English
  const [suggestions, setSuggestions] = useState([
    "I want to build a digital solution for my business",
    "I'm looking to rent an apartment in Porto for my vacation",
    "How can I get in touch with your team?"
  ]);
  const [isChatVisible, setIsChatVisible] = useState(true);
  const recognitionRef = useRef(null);
  const inputRef = useRef(null);
  const [companyData, setCompanyData] = useState(null);
  const apiBaseUrl = process.env.REACT_APP_API_URL || 'http://localhost:5001';
  const apiKey = process.env.REACT_APP_API_KEY || '';
  const [showScrollIndicator, setShowScrollIndicator] = useState(false);
  const messagesContainerRef = useRef(null);

  // Language detection function
  const detectLanguage = useCallback((text) => {
    // Simple language detection based on common words and patterns
    const lowerText = text.toLowerCase();

    // Portuguese patterns
    if (lowerText.match(/^(olá|ola|bom dia|boa tarde|boa noite|obrigado|obrigada|como|quero|gostaria|preciso)/i) ||
        lowerText.includes('português') ||
        (lowerText.includes('apartamento') && lowerText.includes('alugar'))) {
      return 'pt';
    }

    // Spanish patterns
    if (lowerText.match(/^(hola|buenos días|buenas tardes|buenas noches|gracias|como|quiero|quisiera|necesito)/i) ||
        lowerText.includes('español') ||
        (lowerText.includes('apartamento') && lowerText.includes('alquilar'))) {
      return 'es';
    }

    // French patterns
    if (lowerText.match(/^(bonjour|bonsoir|merci|salut|comment|je veux|j'aimerais|j'ai besoin)/i) ||
        lowerText.includes('français') ||
        (lowerText.includes('appartement') && lowerText.includes('louer'))) {
      return 'fr';
    }

    // German patterns
    if (lowerText.match(/^(hallo|guten tag|guten morgen|guten abend|danke|wie|ich möchte|ich brauche)/i) ||
        lowerText.includes('deutsch') ||
        (lowerText.includes('wohnung') && lowerText.includes('mieten'))) {
      return 'de';
    }

    // Default to English
    return 'en';
  }, []);

  // Update suggestions based on detected language - wrapped in useCallback
  const updateSuggestionsByLanguage = useCallback((language) => {
    if (suggestionsByLanguage[language]) {
      setSuggestions(suggestionsByLanguage[language]);
      setCurrentLanguage(language);
    }
  }, []);

  // Fetch optimized company data on component mount
  useEffect(() => {
    // Clear cache to ensure we get the latest system prompt
    const clearCache = () => {
      localStorage.removeItem('companyData');
      localStorage.removeItem('companyDataTimestamp');
    };

    // Clear cache on first load to ensure we get the latest system prompt
    clearCache();

    const fetchCompanyData = async () => {
      try {
        // Check if we have cached data that's less than 1 hour old
        const cachedData = localStorage.getItem('companyData');
        const cachedTimestamp = localStorage.getItem('companyDataTimestamp');
        const oneHourAgo = Date.now() - (60 * 60 * 1000);

        // In development mode, always fetch fresh data to make editing easier
        const isDevelopment = process.env.NODE_ENV === 'development';

        if (!isDevelopment && cachedData && cachedTimestamp && parseInt(cachedTimestamp) > oneHourAgo) {
          // Use cached data if it's recent and not in development
          const parsedData = JSON.parse(cachedData);

          // Validate the parsed data has the required fields
          if (parsedData && parsedData.system_prompt) {
            setCompanyData(parsedData);
          } else {
            // If cached data is invalid, fetch fresh data
            throw new Error('Cached company data is invalid');
          }
        } else {
          // Fetch fresh data
          const response = await axios.get(`${apiBaseUrl}/api/optimized-company-data`);

          // Validate the response data
          if (response.data && response.data.system_prompt) {
            setCompanyData(response.data);

            // Cache the data
            localStorage.setItem('companyData', JSON.stringify(response.data));
            localStorage.setItem('companyDataTimestamp', Date.now().toString());
          } else {
            throw new Error('Received invalid company data from API');
          }
        }
      } catch (error) {
        // Set a default system prompt as fallback
        setCompanyData({
          system_prompt: "I'm an AI assistant for MUS. How can I help you today?",
          company_data: {
            name: "MUS",
            contact: {
              email: "info@mus.pt"
            },
            staff: [],
            services: []
          }
        });
      }
    };

    fetchCompanyData();
  }, [apiBaseUrl]);

  // Scroll to bottom when new messages are added - no longer needed with reversed order
  useEffect(() => {
    // No need to scroll as the latest message will be at the top
  }, [messages]);

  // Cycle through placeholders
  useEffect(() => {
    const interval = setInterval(() => {
      setShowPlaceholder(false);
      setTimeout(() => {
        setPlaceholderIndex(prevIndex => (prevIndex + 1) % placeholders.length);
        setShowPlaceholder(true);
      }, 300);
    }, 3000);

    return () => clearInterval(interval);
  }, []);

  // Initialize speech recognition with language support
  useEffect(() => {
    if (window.webkitSpeechRecognition) {
      const SpeechRecognition = window.webkitSpeechRecognition;
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = false;
      recognitionRef.current.interimResults = false;

      // Set language based on current UI language
      if (currentLanguage === 'pt') {
        recognitionRef.current.lang = 'pt-PT';
      } else if (currentLanguage === 'es') {
        recognitionRef.current.lang = 'es-ES';
      } else if (currentLanguage === 'fr') {
        recognitionRef.current.lang = 'fr-FR';
      } else if (currentLanguage === 'de') {
        recognitionRef.current.lang = 'de-DE';
      } else {
        recognitionRef.current.lang = 'en-US';
      }

      recognitionRef.current.onresult = (event) => {
        if (event.results.length > 0) {
          const transcript = event.results[0][0].transcript;
          setInputValue(transcript);

          // Auto-send after a short delay
          if (transcript.trim()) {
            setTimeout(() => {
              if (inputRef.current) {
                const inputEvent = new Event('input', { bubbles: true });
                inputRef.current.dispatchEvent(inputEvent);

                // Trigger send
                const enterEvent = new KeyboardEvent('keydown', {
                  key: 'Enter',
                  code: 'Enter',
                  keyCode: 13,
                  which: 13,
                  bubbles: true
                });
                inputRef.current.dispatchEvent(enterEvent);

                // Start listening again after a short delay
                setTimeout(() => {
                  recognitionRef.current.start();
                }, 100);
              }
            }, 1000); // Adicionando o tempo de espera que estava faltando
          }
        }
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };
    }
  }, [currentLanguage, isListening, updateSuggestionsByLanguage]);

  // Update toggleListening to use the current language
  const toggleListening = () => {
    if (isListening) {
      recognitionRef.current?.stop();
      setIsListening(false);
    } else {
      // Update language before starting
      if (recognitionRef.current) {
        switch (currentLanguage) {
          case 'pt':
            recognitionRef.current.lang = 'pt-PT';
            break;
          case 'es':
            recognitionRef.current.lang = 'es-ES';
            break;
          case 'fr':
            recognitionRef.current.lang = 'fr-FR';
            break;
          case 'de':
            recognitionRef.current.lang = 'de-DE';
            break;
          default:
            recognitionRef.current.lang = 'en-US';
        }
      }
      recognitionRef.current?.start();
      setIsListening(true);
    }
  };

  // Update the event listener to handle the game controls state
  useEffect(() => {
    const handleToggleChat = (event) => {
      if (event.detail.source === 'gamepadIcon') {
        // If controls are visible, hide chat. If controls are hidden, show chat
        setIsChatVisible(!event.detail.controlsVisible);
      }
    };

    document.addEventListener('toggleChat', handleToggleChat);
    return () => {
      document.removeEventListener('toggleChat', handleToggleChat);
    };
  }, []);

  // Show chat and hide game controls when user interacts with input
  const handleInputChange = (e) => {
    setInputValue(e.target.value);

    // Show chat if it's not visible
    if (!isChatVisible) {
      setIsChatVisible(true);
    }

    // Hide game controls when typing
    const event = new CustomEvent('toggleGameControls', {
      detail: { visible: false }
    });
    document.dispatchEvent(event);

    // Detect language as user types (only if input is substantial)
    if (e.target.value.trim().length > 5) {
      const detectedLanguage = detectLanguage(e.target.value);
      if (detectedLanguage !== currentLanguage) {
        updateSuggestionsByLanguage(detectedLanguage);
      }
    }
  };

  // Update the suggestion click handler to hide game controls
  const handleSuggestionClick = (suggestion) => {
    // Always show chat when clicking a suggestion
    setIsChatVisible(true);

    // Hide game controls by dispatching an event
    const event = new CustomEvent('toggleGameControls', {
      detail: { visible: false }
    });
    document.dispatchEvent(event);

    setInputValue(suggestion);
    sendMessage(null, suggestion);
  };

  const callMistralAPI = async (userMessage) => {
    // Validate message content
    if (!userMessage || !userMessage.content || userMessage.content.trim() === '') {
      return;
    }

    setIsLoading(true);

    try {
      // Prepare messages array with system message if needed
      let messageHistory = [...messages, userMessage];

      // If we have company data and no system message yet, add it
      if (companyData && !messageHistory.some(msg => msg.role === 'system')) {
        // Create a basic system message
        const systemMessage = {
          role: 'system',
          content: companyData.system_prompt || '' // Ensure system prompt is never undefined
        };

        messageHistory = [systemMessage, ...messageHistory];
      }

      // Validate all messages have content before sending
      const validatedMessages = messageHistory.map(msg => ({
        role: msg.role,
        content: msg.content || '' // Ensure content is never undefined
      })).filter(msg => msg.content.trim() !== ''); // Filter out any empty messages

      // Compress chat history if it's getting long (client-side compression)
      let compressedMessages = validatedMessages;
      if (compressedMessages.length > 15) { // Increased threshold from 10 to 15
        // Keep system messages and recent exchanges
        const systemMessages = compressedMessages.filter(msg => msg.role === 'system');
        const nonSystemMessages = compressedMessages.filter(msg => msg.role !== 'system');

        // Check if the last user message is very short (like "Yes" or "No")
        const lastUserMessage = nonSystemMessages.filter(msg => msg.role === 'user').pop();
        const isShortResponse = lastUserMessage && lastUserMessage.content.trim().length <= 5;

        // Keep more context for short responses (last 5 exchanges = 10 messages)
        // Otherwise keep last 4 exchanges (8 messages)
        const messagesToKeep = isShortResponse ? 10 : 8;
        const recentMessages = nonSystemMessages.slice(-messagesToKeep);

        compressedMessages = [...systemMessages, ...recentMessages];
      }

      // Ensure we have at least one message
      if (compressedMessages.length === 0) {
        throw new Error('No valid messages to send');
      }

      // Call our backend API
      const response = await axios.post(
        `${apiBaseUrl}/api/chat`,
        {
          messages: compressedMessages
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'x-api-key': apiKey
          }
        }
      );

      // Extract the assistant's response
      const assistantResponse = response.data.response;
      const assistantMessage = {
        role: 'assistant',
        content: assistantResponse || "I'm sorry, I couldn't generate a response."
      };

      // Update messages with the assistant's response
      setMessages(prev => [...prev, assistantMessage]);

      // Detect language from assistant's response
      const assistantLanguage = detectLanguage(assistantResponse);
      if (assistantLanguage !== currentLanguage) {
        updateSuggestionsByLanguage(assistantLanguage);
      }

      // Update suggestions if available from API
      if (response.data.suggestions && Array.isArray(response.data.suggestions)) {
        // Only use API suggestions if they're in the same language
        if (response.data.suggestions.length > 0) {
          setSuggestions(response.data.suggestions);
        }
      }
    } catch (error) {
      // Extract error message from API response if available
      let errorMessage = 'Sorry, I encountered an error when calling the API. Please try again later.';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      } else if (error.response?.data?.details) {
        errorMessage = error.response.data.details;
      } else if (error.message) {
        errorMessage = error.message;
      }

      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: errorMessage
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Update sendMessage to detect language
  const sendMessage = async (e, suggestionText) => {
    // Use suggestion text if provided, otherwise use input value
    const messageText = suggestionText || inputValue;

    // Trim the input value to check if it's actually empty
    const trimmedInput = messageText.trim();

    if (trimmedInput === '' || isLoading) return;

    // Detect language and update suggestions
    const detectedLanguage = detectLanguage(trimmedInput);
    if (detectedLanguage !== currentLanguage) {
      updateSuggestionsByLanguage(detectedLanguage);
    }

    // Create a user message object
    const userMessage = {
      role: 'user',
      content: trimmedInput
    };

    // Add the user message to the messages array
    setMessages(prev => [...prev, userMessage]);

    // Clear the input field
    setInputValue('');

    // Call the API with the user message
    try {
      setIsLoading(true);
      await callMistralAPI(userMessage);
    } catch (error) {
      console.error('Error calling API:', error);
      // Add an error message
      setMessages(prev => [
        ...prev,
        {
          role: 'assistant',
          content: "I'm sorry, I encountered an error. Please try again later."
        }
      ]);
    } finally {
      setIsLoading(false);
    }
  };

  // Update handleKeyDown to hide game controls when user presses Enter
  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();

      // Show chat if it's not visible
      if (!isChatVisible) {
        setIsChatVisible(true);
      }

      // Hide game controls
      const event = new CustomEvent('toggleGameControls', {
        detail: { visible: false }
      });
      document.dispatchEvent(event);

      sendMessage();
    }
  };

  const hasMessages = messages.length > 0 || isLoading;
  const shouldShowPlaceholder = showPlaceholder && !inputValue;

  // Function to clear cache and reload company data
  const refreshCompanyData = async () => {
    // Clear local storage
    localStorage.removeItem('companyData');
    localStorage.removeItem('companyDataTimestamp');

    try {
      // Fetch fresh data
      const response = await axios.get(`${apiBaseUrl}/api/optimized-company-data`);

      if (response.data && response.data.system_prompt) {
        setCompanyData(response.data);

        // Show a temporary message in the chat
        setMessages(prev => [
          ...prev,
          {
            role: 'assistant',
            content: 'Company data refreshed. I now have the latest information.'
          }
        ]);
      }
    } catch (error) {
      // Error handling without console.error
    }
  };

  // Check if messages container needs scrolling
  useEffect(() => {
    if (messagesContainerRef.current) {
      const { scrollHeight, clientHeight } = messagesContainerRef.current;
      setShowScrollIndicator(scrollHeight > clientHeight + 50);

      // Hide scroll indicator after 5 seconds
      if (scrollHeight > clientHeight + 50) {
        const timer = setTimeout(() => {
          setShowScrollIndicator(false);
        }, 5000);

        return () => clearTimeout(timer);
      }
    }
  }, [messages, isChatVisible]);

  return (
    <MainContainer>
      <RefreshButton onClick={refreshCompanyData} title="Refresh company data">
        &gt;
      </RefreshButton>

      <InputContainer>
        <MessageInput
          type="text"
          placeholder=""
          value={inputValue}
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          disabled={isLoading}
          ref={inputRef}
        />
        {shouldShowPlaceholder && (
          <PlaceholderText $show={shouldShowPlaceholder}>
            {placeholders[placeholderIndex]}
          </PlaceholderText>
        )}
        <MicrophoneButton
          isListening={isListening}
          onClick={toggleListening}
          disabled={isLoading}
        />
        <SendButton onClick={sendMessage} disabled={isLoading}>
          <SendIcon src={sendIcon} alt="Send" />
        </SendButton>
      </InputContainer>

      <DisclaimerNote $visible={isChatVisible}>
        Responses are created by AI and may contain mistakes.
      </DisclaimerNote>

      {hasMessages && (
        <ChatContainer $visible={isChatVisible}>
          <MessagesContainer ref={messagesContainerRef}>
            {/* Display messages in reverse order (newest first) */}
            {isLoading && (
              <Message
                $role="assistant"
                content="Thinking..."
                isLoading={true}
              />
            )}
            {messages.slice().reverse().map((message, index) => (
              <Message
                key={index}
                $role={message.role}
                content={message.content}
                isLoading={false}
              />
            ))}
          </MessagesContainer>
        </ChatContainer>
      )}

      <ScrollIndicator $visible={showScrollIndicator && isChatVisible}>
        Scroll to see more
      </ScrollIndicator>

      <SuggestionsContainer>
        {suggestions.map((suggestion, index) => (
          <SuggestionButton
            key={index}
            text={suggestion}
            onClick={() => handleSuggestionClick(suggestion)}
          />
        ))}
      </SuggestionsContainer>
    </MainContainer>
  );
};

export default ChatInterface;
